/**
 * Canonical TanStack Query key for a dataset produced by a tool call.
 *
 * Shape: `['tool-dataset', threadId, toolName, args]`.
 *
 * - `threadId` scopes every key to the owning chat thread, so dataset caches
 *   from different threads cannot collide.
 * - `toolName` + `args` together identify the producer call. Re-running the
 *   same producer with the same args yields the same cached dataset, which
 *   is the inversion of control the resolver relies on: a downstream tool
 *   asking for an "upstream outputDataset" by name resolves to the same
 *   `(toolName, args)` tuple and hits the same cache entry every time.
 *
 * `args` is included by reference — TanStack Query hashes keys
 * deterministically (object-hash), so structurally identical args produce
 * identical keys regardless of property order.
 */

/** Root tag at index 0 — used to bulk-invalidate every dataset key for a thread. */
export const DATASET_QUERY_KEY_ROOT = 'tool-dataset' as const;

/** Canonical tuple shape for a dataset query key. */
export type DatasetQueryKey = readonly [
  typeof DATASET_QUERY_KEY_ROOT,
  threadId: string,
  toolName: string,
  args: Record<string, unknown>,
];

/**
 * Build the canonical dataset query key.
 *
 * Prefer this helper at every call site so the key shape stays a single
 * source of truth. Manual `['tool-dataset', ...]` literals drift over time.
 */
export function datasetQueryKey(threadId: string, toolName: string, args: Record<string, unknown>): DatasetQueryKey {
  return [DATASET_QUERY_KEY_ROOT, threadId, toolName, args] as const;
}

/**
 * Partial key matching every dataset produced in a thread. Pass to
 * `queryClient.removeQueries({ queryKey })` on thread delete.
 */
export function threadDatasetKeyPrefix(threadId: string): readonly [typeof DATASET_QUERY_KEY_ROOT, string] {
  return [DATASET_QUERY_KEY_ROOT, threadId] as const;
}
